import { loadMathJax } from "../load-mathjax.js";
import { placeSelectionInsideBlock } from "../selection.js";
import { ManuscriptWidget } from "./base.js";

export class MathWidget extends ManuscriptWidget {
    destroyed = false;

    constructor(itemOrMath, displayMode = true, preamble = "") {
        super();
        if (typeof itemOrMath === "object" && itemOrMath !== null) {
            this.item = itemOrMath;
            this.math = itemOrMath.text;
            this.displayMode = true;
            this.preamble = "";
        } else {
            this.item = null;
            this.math = itemOrMath;
            this.displayMode = displayMode;
            this.preamble = preamble;
        }
        this.cachedHeight = undefined;
    }

    eq(other) {
        return other.math === this.math && other.displayMode === this.displayMode && other.preamble === this.preamble;
    }

    toDOM(view) {
        this.destroyed = false;
        const element = document.createElement(this.displayMode ? "div" : "span");
        element.className = "ol-cm-math";
        element.style.height = `${this.estimatedHeight}px`;
        if (this.displayMode) {
            element.addEventListener("mouseup", (event) => {
                event.preventDefault();
                view.dispatch(placeSelectionInsideBlock(view, event));
            });
        }

        this.renderMath(element)
            .catch(() => {
                element.classList.add("ol-cm-math-error");
                this.renderFallback(element);
            })
            .finally(() => {
                view.requestMeasure();
            });

        return element;
    }

    updateDOM(element, view) {
        this.destroyed = false;
        this.renderMath(element)
            .catch(() => {
                element.classList.add("ol-cm-math-error");
                this.renderFallback(element);
            })
            .finally(() => {
                view.requestMeasure();
            });
        return true;
    }

    ignoreEvent(event) {
        if (event.type === "mouseup") {
            return false;
        }
        if (!this.displayMode && event.type === "mousedown") {
            return false;
        }
        return true;
    }

    destroy() {
        this.destroyed = true;
    }

    get estimatedHeight() {
        return this.cachedHeight ?? this.math.split("\n").length * 40;
    }

    coordsAt(element) {
        return element.getBoundingClientRect();
    }

    async renderMath(element) {
        const MathJax = await loadMathJax();

        if (this.destroyed || !element.isConnected) {
            return;
        }

        MathJax.texReset?.([0]);
        if (this.preamble) {
            try {
                await MathJax.tex2svgPromise(this.preamble);
            } catch (_error) {}
        }

        if (this.destroyed || !element.isConnected) {
            return;
        }

        const math = await MathJax.tex2svgPromise(this.math, {
            ...(MathJax.getMetricsFor ? MathJax.getMetricsFor(element, this.displayMode) : {}),
            display: this.displayMode,
        });

        if (this.destroyed || !element.isConnected) {
            return;
        }

        element.replaceChildren(math);
        element.style.height = "auto";
        this.cachedHeight = element.offsetHeight || this.estimatedHeight;
    }

    renderFallback(element) {
        element.textContent = "";
        const inner = document.createElement(this.displayMode ? "div" : "span");
        inner.className = "ol-cm-math-content";
        inner.textContent = this.math;
        element.appendChild(inner);
        element.style.height = this.displayMode ? "auto" : "";
        this.cachedHeight = element.offsetHeight || this.estimatedHeight;
    }
}
